import type { ReactNode } from "react";
// biome-ignore lint/style/noRestrictedImports: legacy suppression
import { Navigate, useLocation } from "react-router-dom";

import { AppShellSkeleton } from "src/components/loading";
import { useUser } from "src/contexts/user-context";
import {
  buildReturnToUrl,
  getCurrentRelativeUrl,
} from "src/utils/auth-redirect";

type Props = {
  children: ReactNode;
  /**
   * Override the fallback rendered while user data is loading. Pre-workspace
   * routes (e.g. `/workspaces`, `/welcome`, `/mcp-connect`) should pass a
   * fallback that matches their layout, since the default `AppShellSkeleton`
   * draws an in-workspace sidebar that doesn't apply here.
   */
  loadingFallback?: ReactNode;
};

export const AuthenticatedRoute = ({
  children,
  loadingFallback = <AppShellSkeleton />,
}: Props) => {
  const { user, isLoading, isCompanyRequired } = useUser();
  const location = useLocation();

  if (isLoading) {
    return <>{loadingFallback}</>;
  }

  if (user) {
    if (isCompanyRequired) {
      if (location.pathname.includes("/restricted")) {
        return <>{children}</>;
      }
      if (
        location.pathname === "/welcome" ||
        location.pathname === "/workspaces"
      ) {
        return <Navigate state={location?.state} to="/restricted" />;
      }
      return (
        <Navigate
          state={location?.state}
          to={buildReturnToUrl("/restricted", getCurrentRelativeUrl(location))}
        />
      );
    }
    return <>{children}</>;
  }
  return (
    <Navigate
      state={location?.state}
      to={buildReturnToUrl("/login", getCurrentRelativeUrl(location))}
    />
  );
};
